import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Activity,
  Radio,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Zap,
  TrendingUp,
  Shield,
  Clock,
  ArrowUpRight,
  Eye,
  TestTube,
  Users,
  MessageSquare,
  Server
} from 'lucide-react';

import { 
  getMonitoringStats, 
  getTenantsStats, 
  getEventFeed, 
  triggerTestEvent 
} from '@/lib/api';

// ==================== HELPER COMPONENTS ====================

const StatusDot = ({ status }) => {
  const colors = {
    healthy: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]',
    degraded: 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]',
    critical: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)] animate-pulse',
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500 animate-pulse',
    info: 'bg-cyan-500',
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[status] || colors.info}`} />;
};

const StatCard = ({ icon: Icon, label, value, subtitle, accent = 'cyan' }) => {
  const accentMap = {
    cyan: 'from-cyan-500/10 to-cyan-500/5 border-cyan-500/20',
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20',
    amber: 'from-amber-500/10 to-amber-500/5 border-amber-500/20',
    red: 'from-red-500/10 to-red-500/5 border-red-500/20',
    violet: 'from-violet-500/10 to-violet-500/5 border-violet-500/20',
  };
  const iconColor = {
    cyan: 'text-cyan-400',
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    red: 'text-red-400',
    violet: 'text-violet-400',
  };

  return (
    <div className={`rounded-xl border bg-gradient-to-br ${accentMap[accent]} p-5 flex items-start gap-4`}>
      <div className={`p-2.5 rounded-lg bg-background/50 ${iconColor[accent]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-foreground mt-0.5">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

const EventRow = ({ event }) => {
  const icons = {
    webhook_received: <MessageSquare className="w-4 h-4 text-cyan-400" />,
    webhook_routed: <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
    webhook_failed: <XCircle className="w-4 h-4 text-red-400" />,
    webhook_unmatched: <AlertTriangle className="w-4 h-4 text-amber-400" />,
    tenant_created: <Users className="w-4 h-4 text-violet-400" />,
    bot_deployed: <Zap className="w-4 h-4 text-cyan-400" />,
    bot_error: <XCircle className="w-4 h-4 text-red-400" />,
  };
  
  const statusColors = {
    success: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    warning: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    error: 'text-red-400 bg-red-500/10 border-red-500/30',
    info: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  };

  const timeAgo = (isoStr) => {
    if (!isoStr) return '';
    const diff = Date.now() - new Date(isoStr).getTime();
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/30 transition-colors group border-b border-border/30 last:border-b-0">
      <div className="flex-shrink-0">
        {icons[event.event_type] || <Activity className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground truncate">{event.details}</p>
        <div className="flex items-center gap-2 mt-1">
          {event.tenant_name && (
            <span className="text-xs text-muted-foreground font-medium">{event.tenant_name}</span>
          )}
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusColors[event.status] || ''}`}>
            {event.status}
          </Badge>
        </div>
      </div>
      <div className="flex-shrink-0 text-xs text-muted-foreground/60 font-mono">
        {timeAgo(event.timestamp)}
      </div>
    </div>
  );
};


// ==================== MAIN PAGE ====================

const MonitoringPage = () => {
  const [summary, setSummary] = useState(null);
  const [tenantStats, setTenantStats] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filterType, setFilterType] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, statsRes, feedRes] = await Promise.all([
        getMonitoringStats(),
        getTenantsStats(),
        getEventFeed(100, filterType)
      ]);
      
      setSummary(summaryRes.data);
      setTenantStats(statsRes.data);
      setFeed(feedRes.data);
      
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching monitoring data:', err);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 8 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 8000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const handleTestEvent = async () => {
    try {
      const res = await triggerTestEvent();
      toast.success(res.data.message || 'Evento de prueba generado');
      fetchData();
    } catch (err) {
      toast.error('Error al generar evento de prueba');
    }
  };

  const healthColor = {
    healthy: 'text-emerald-400',
    degraded: 'text-amber-400',
    critical: 'text-red-400',
  };

  const healthLabel = {
    healthy: 'Sistema Operacional',
    degraded: 'Alertas Activas',
    critical: 'Estado Crítico',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="loading-spinner w-10 h-10 mx-auto" />
          <p className="text-muted-foreground">Conectando con la Torre de Control...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="monitoring-page">
      
      {/* Header V4.3 - ADN Fluxo Co */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 pt-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <Radio className="w-5 h-5 text-blue-500" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Centro de Comando</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
            Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Control Tower</span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium italic text-sm">Monitoreo de flujos, telemetría de eventos y salud del Switchboard en tiempo real</p>
        </motion.div>

        <div className="flex items-center gap-3">
          {summary && (
            <div className={`flex items-center gap-3 bg-[#0F172A]/60 backdrop-blur-xl p-3 px-5 rounded-[2rem] border ${summary.platform_health === 'healthy' ? 'border-emerald-500/20' : 'border-red-500/20'} shadow-2xl shadow-black/40 overflow-hidden relative group`}>
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className={`w-3 h-3 rounded-full ${summary.platform_health === 'healthy' ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.4)]' : 'bg-red-500 animate-pulse shadow-[0_0_12px_rgba(239,68,68,0.4)]'}`} />
              <span className={`font-black text-[10px] uppercase tracking-widest ${summary.platform_health === 'healthy' ? 'text-emerald-400' : 'text-red-400'}`}>
                {healthLabel[summary.platform_health]}
              </span>
            </div>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`h-11 rounded-2xl px-5 border border-white/5 backdrop-blur-xl transition-all ${autoRefresh ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-slate-900/50 text-slate-500 hover:text-white'}`}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} style={autoRefresh ? {animationDuration: '3s'} : {}} />
            <span className="text-[10px] font-black uppercase tracking-widest">{autoRefresh ? 'LIVE FEED' : 'PAUSADO'}</span>
          </Button>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleTestEvent}
            className="h-11 rounded-2xl px-5 border border-white/5 bg-slate-900/50 text-slate-400 hover:text-white hover:bg-white/5"
          >
            <TestTube className="w-4 h-4 mr-2" />
            <span className="text-[10px] font-black uppercase tracking-widest">SIMULAR EVENTO</span>
          </Button>
        </div>
      </div>

      {/* ==================== STATS GRID ==================== */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard 
            icon={Activity} 
            label="Eventos Hoy" 
            value={summary.events_today} 
            subtitle={`${summary.events_last_hour} última hora`}
            accent="cyan" 
          />
          <StatCard 
            icon={CheckCircle2} 
            label="Total Histórico" 
            value={summary.total_events_all_time} 
            accent="emerald" 
          />
          <StatCard 
            icon={AlertTriangle} 
            label="Errores Hoy" 
            value={summary.errors_today} 
            subtitle={`${summary.error_rate_today}% tasa de error`}
            accent={summary.errors_today > 0 ? "red" : "emerald"} 
          />
          <StatCard 
            icon={Users} 
            label="Tenants Activos" 
            value={summary.active_tenants} 
            subtitle={`de ${summary.total_tenants} totales`}
            accent="violet" 
          />
          <StatCard 
            icon={Shield} 
            label="Salud" 
            value={summary.platform_health === 'healthy' ? '100%' : summary.platform_health === 'degraded' ? '~80%' : '<50%'} 
            subtitle={summary.platform_health}
            accent={summary.platform_health === 'healthy' ? "emerald" : "amber"} 
          />
        </div>
      )}

      {/* ==================== MAIN GRID: TENANT STATS + LIVE FEED ==================== */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        
        {/* TENANT STATS (Left 2/5) */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="glass-card border-primary/10">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                  <CardTitle className="text-lg">Stats por Tenant</CardTitle>
                </div>
                <Badge variant="outline" className="text-xs">{tenantStats.length} activos</Badge>
              </div>
              <CardDescription>Métricas de actividad por negocio</CardDescription>
            </CardHeader>
            <CardContent>
              {tenantStats.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Server className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">Sin datos de tenants aún.</p>
                  <p className="text-xs mt-1">Genera eventos de prueba para poblar.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1 custom-scrollbar">
                  {tenantStats.map((stat) => (
                    <div key={stat.tenant_id} className="p-4 rounded-xl border border-border/50 bg-secondary/20 hover:bg-secondary/40 transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <StatusDot status={stat.health} />
                          <span className="font-semibold text-foreground text-sm">{stat.tenant_name}</span>
                        </div>
                        <Badge variant={stat.health === 'healthy' ? 'secondary' : 'destructive'} className="text-[10px]">
                          {stat.health}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="text-center p-2 rounded-lg bg-background/50">
                          <p className="text-lg font-bold text-cyan-400">{stat.today_messages}</p>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Hoy</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-background/50">
                          <p className="text-lg font-bold text-foreground">{stat.total_messages}</p>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</p>
                        </div>
                      </div>
                      
                      {stat.today_errors > 0 && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-400">
                          <AlertTriangle className="w-3 h-3" />
                          <span>{stat.today_errors} error(es) hoy</span>
                        </div>
                      )}
                      
                      {stat.last_activity && (
                        <p className="mt-2 text-[10px] text-muted-foreground/60 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Última actividad: {new Date(stat.last_activity).toLocaleString('es-MX', { 
                            hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' 
                          })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* LIVE FEED (Right 3/5) */}
        <div className="lg:col-span-3">
          <Card className="glass-card border-primary/10">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-cyan-400" />
                  <CardTitle className="text-lg">Live Feed</CardTitle>
                  {autoRefresh && (
                    <span className="flex items-center gap-1 text-xs text-emerald-400 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      EN VIVO
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {['all', 'webhook_routed', 'webhook_failed', 'webhook_unmatched'].map((type) => (
                    <button 
                      key={type} 
                      onClick={() => setFilterType(type)}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${filterType === type ? 'bg-cyan-600 text-white' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'}`}
                    >
                      {type === 'all' ? 'Todo' : type === 'webhook_routed' ? '✓ OK' : type === 'webhook_failed' ? '✗ Error' : '? Sin Match'}
                    </button>
                  ))}
                </div>
              </div>
              <CardDescription>
                Flujo de actividad del Switchboard · {feed.length} eventos
                {lastRefresh && (
                  <span className="ml-2 text-muted-foreground/50">
                    · Actualizado {lastRefresh.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {feed.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                  <Activity className="w-12 h-12 mx-auto mb-4 opacity-15" />
                  <p className="text-sm font-medium">Sin actividad registrada</p>
                  <p className="text-xs mt-2 max-w-sm mx-auto">
                    Usa el botón "Simular Evento" para generar datos de prueba,
                    o envía un mensaje de WhatsApp real a través del Switchboard.
                  </p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={handleTestEvent}>
                    <TestTube className="w-4 h-4 mr-1.5" />
                    Generar Primer Evento
                  </Button>
                </div>
              ) : (
                <div className="max-h-[550px] overflow-y-auto custom-scrollbar rounded-lg border border-border/30 bg-background/30">
                  {feed.map((event, idx) => (
                    <EventRow key={`${event.timestamp}-${idx}`} event={event} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ==================== ALERTS SECTION ==================== */}
      {summary?.recent_alerts?.length > 0 && (
        <Card className="glass-card border-red-500/30 bg-gradient-to-br from-red-500/5 to-background">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />
              <CardTitle className="text-lg text-red-400">Alertas Recientes</CardTitle>
              <Badge variant="destructive" className="text-xs">{summary.recent_alerts.length}</Badge>
            </div>
            <CardDescription>Errores detectados en la última hora</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.recent_alerts.map((alert, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{alert.details}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {alert.tenant_name && `${alert.tenant_name} · `}
                      {new Date(alert.timestamp).toLocaleString('es-MX')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default MonitoringPage;
